/**
 * LearnMore.jsx — Marketing / Feature Showcase Page for RevTracker
 * 
 * Based on ticketasaurus.com template
 * Screenshots go in /public/screenshots/
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ChevronLeft, ChevronRight, Circle,
  Smartphone, X, ZoomIn,
  TrendingUp, DollarSign, Receipt, BarChart3, 
  FileText, Shield, Calculator, PieChart,
  Wallet, Clock, Users, CheckCircle, Car
} from 'lucide-react';


// ============================================================
// CONFIG — RevTracker specific
// ============================================================
const APP_NAME = "RevTracker";
const HERO_TITLE = "Professional Financial Management";
const HERO_SUBTITLE = "Track income, expenses, and generate Schedule C tax reports with ease. Built for freelancers, contractors, and small business owners.";
const CTA_HEADLINE = "Ready to Take Control of Your Finances?";
const CTA_SUBTITLE = "Join thousands of professionals who trust RevTracker for their financial management.";
const CTA_BUTTON_TEXT = "Get Started";
const CTA_LINK = "/login";
const BACK_LINK = "/login";
const AUTO_PLAY_INTERVAL = 5000;


// ============================================================
// SLIDES — Replace with actual screenshots
// ============================================================
const slides = [
  {
    mobile: true,
    images: ["/screenshots/login_biometric.png", "/screenshots/home_dashboard.png"],
    title: "Mobile Ready",
    desc: "Full-featured mobile experience with biometric login. Track your finances on the go — no app download needed.",
    icon: Smartphone,
    accent: "from-cyan-500 to-blue-500"
  },
  {
    image: "/screenshots/desktop_dashboard.png",
    title: "Dashboard Overview",
    desc: "See your complete financial picture at a glance. Income, expenses, net profit, taxes paid, and balance owed — all in one place.",
    icon: TrendingUp,
    accent: "from-blue-500 to-cyan-500"
  },
  {
    mobile: true,
    images: ["/screenshots/mileage_tracking.png", "/screenshots/add_income.png"],
    title: "Track On The Go",
    desc: "GPS mileage tracking with automatic expense logging. Add income entries with customer, payment type, and receipt uploads.",
    icon: Car,
    accent: "from-emerald-500 to-teal-500"
  },
  {
    image: "/screenshots/desktop_schedule_c.png",
    title: "Schedule C Reports",
    desc: "Generate IRS-ready Schedule C (Form 1040) reports with all income and expenses properly categorized by line item.",
    icon: FileText,
    accent: "from-purple-500 to-pink-500"
  },
  {
    image: "/screenshots/desktop_analytics.png",
    title: "Analytics & Insights",
    desc: "Visualize income vs expenses over time. See top income sources and expense categories with interactive charts.",
    icon: BarChart3,
    accent: "from-pink-500 to-rose-500"
  },
  {
    mobile: true,
    images: ["/screenshots/tax_summary.png", "/screenshots/home_dashboard.png"],
    title: "Tax Summary & Estimation",
    desc: "Real-time tax calculations with estimated quarterly payments. Track tax deposits made and remaining balance owed.",
    icon: Calculator,
    accent: "from-orange-500 to-red-500"
  },
  {
    image: "/screenshots/desktop_managers.png",
    title: "Team Management",
    desc: "Add managers with custom permissions to help manage your books. Control who can view or edit your financial data.",
    icon: Users,
    accent: "from-indigo-500 to-blue-500"
  },
];


// ============================================================
// FEATURES GRID
// ============================================================
const features = [
  { icon: TrendingUp, title: "Dashboard", desc: "Complete financial overview" },
  { icon: DollarSign, title: "Income", desc: "Track all revenue sources" },
  { icon: Receipt, title: "Expenses", desc: "Categorize deductions" },
  { icon: Car, title: "Mileage", desc: "GPS mileage tracking" },
  { icon: FileText, title: "Schedule C", desc: "IRS-ready tax reports" },
  { icon: Calculator, title: "Tax Estimates", desc: "Quarterly tax planning" },
  { icon: PieChart, title: "Analytics", desc: "Visual insights" },
  { icon: Wallet, title: "Checkbook", desc: "Transaction register" },
  { icon: Users, title: "Customers", desc: "Client management" },
  { icon: Shield, title: "Secure", desc: "Bank-level security" },
  { icon: Smartphone, title: "Mobile", desc: "Works everywhere" },
  { icon: CheckCircle, title: "Biometric", desc: "Fingerprint login" },
];


// ============================================================
// COMPONENT
// ============================================================
export default function LearnMore() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(AUTO_PLAY_INTERVAL > 0);
  const [direction, setDirection] = useState(1);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [imageErrors, setImageErrors] = useState({});

  // Build flat list of all images for lightbox navigation
  const allImages = slides.reduce((acc, s) => {
    if (s.mobile) {
      s.images.forEach((img) => acc.push({ src: img, title: s.title, desc: s.desc }));
    } else {
      acc.push({ src: s.image, title: s.title, desc: s.desc });
    }
    return acc;
  }, []);

  const openLightbox = (imgSrc) => {
    if (imageErrors[imgSrc]) return; // Don't open lightbox for broken images
    const idx = allImages.findIndex(i => i.src === imgSrc);
    setLightboxIdx(idx >= 0 ? idx : 0);
  };

  const lightboxPrev = () => setLightboxIdx(i => (i - 1 + allImages.length) % allImages.length);
  const lightboxNext = () => setLightboxIdx(i => (i + 1) % allImages.length);

  const goTo = useCallback((idx) => {
    setDirection(idx > current ? 1 : -1);
    setCurrent(idx);
    setIsAutoPlaying(false);
  }, [current]);

  const next = useCallback(() => {
    setDirection(1);
    setCurrent(c => (c + 1) % slides.length);
  }, []);

  const prev = useCallback(() => {
    setDirection(-1);
    setCurrent(c => (c - 1 + slides.length) % slides.length);
    setIsAutoPlaying(false);
  }, []);

  useEffect(() => {
    if (!isAutoPlaying || AUTO_PLAY_INTERVAL <= 0) return;
    const timer = setInterval(next, AUTO_PLAY_INTERVAL);
    return () => clearInterval(timer);
  }, [isAutoPlaying, next]);

  // Handle keyboard navigation in lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIdx === null) return;
      if (e.key === 'ArrowRight') lightboxNext();
      else if (e.key === 'ArrowLeft') lightboxPrev();
      else if (e.key === 'Escape') setLightboxIdx(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIdx]);

  const slide = slides[current];
  const Icon = slide.icon;

  const handleImageError = (imgSrc) => {
    setImageErrors(prev => ({ ...prev, [imgSrc]: true }));
  };

  // Placeholder component for missing images
  const ImagePlaceholder = ({ title, icon: PlaceholderIcon, className = "" }) => (
    <div className={`w-full h-full bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 flex flex-col items-center justify-center ${className}`}>
      <PlaceholderIcon size={48} className="text-cyan-400/60 mb-3" />
      <span className="text-slate-400 text-sm font-medium">{title}</span>
      <span className="text-slate-500 text-xs mt-1">Screenshot coming soon</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-[128px]"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-[128px]"></div>
      </div>

      {/* Nav */}
      <nav className="relative z-10 max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
        <button
          onClick={() => navigate(BACK_LINK)}
          className="text-slate-400 hover:text-white transition-colors text-sm flex items-center gap-2"
          data-testid="learn-more-back-btn"
        >
          <ArrowLeft size={16} /> Back to Login
        </button>
        <div className="text-lg font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-cyan-400" />
          {APP_NAME}
        </div>
        <button
          onClick={() => navigate(CTA_LINK)}
          className="px-4 py-1.5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-sm font-medium rounded-lg transition-all shadow-lg shadow-cyan-500/20"
          data-testid="learn-more-get-started-btn"
        >
          {CTA_BUTTON_TEXT}
        </button>
      </nav>

      {/* Hero */}
      <div className="relative z-10 text-center px-4 pt-6 pb-10">
        <h1 className="text-4xl sm:text-5xl font-bold mb-3 bg-gradient-to-r from-cyan-400 via-blue-300 to-purple-400 bg-clip-text text-transparent">
          {HERO_TITLE}
        </h1>
        <p className="text-base text-slate-400 max-w-2xl mx-auto leading-relaxed">
          {HERO_SUBTITLE}
        </p>
      </div>

      {/* Screenshot Showcase */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 pb-12">
        <div className="relative rounded-2xl overflow-hidden bg-slate-900/50 border border-slate-700/40 shadow-2xl backdrop-blur-sm">
          <div className="flex flex-col lg:flex-row">
            {/* Screenshot */}
            <div className="lg:w-2/3 relative bg-black/30">
              <div className="aspect-[16/10] relative overflow-hidden">
                {slide.mobile ? (
                  <div
                    key={current}
                    className="w-full h-full flex items-center justify-center gap-6 px-8 py-4 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
                    style={{ animation: 'fadeSlide 0.4s ease-out' }}
                  >
                    {slide.images.map((img, i) => (
                      <div key={i} className="relative cursor-pointer group/phone" onClick={() => openLightbox(img)} style={{ height: '92%' }}>
                        <div className="h-full aspect-[9/19.5] bg-slate-950 rounded-[2rem] border-[3px] border-slate-600 overflow-hidden shadow-2xl shadow-cyan-500/10 relative group-hover/phone:border-cyan-500/60 transition-colors">
                          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-5 bg-slate-950 rounded-b-2xl z-10" />
                          {imageErrors[img] ? (
                            <ImagePlaceholder title={`Mobile ${i + 1}`} icon={Smartphone} />
                          ) : (
                            <img 
                              src={img} 
                              alt={`${slide.title} ${i + 1}`} 
                              className="w-full h-full object-cover object-top"
                              onError={() => handleImageError(img)}
                            />
                          )}
                          {!imageErrors[img] && (
                            <div className="absolute inset-0 bg-black/0 group-hover/phone:bg-black/30 transition-all flex items-center justify-center opacity-0 group-hover/phone:opacity-100">
                              <ZoomIn size={28} className="text-white drop-shadow-lg" />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    key={current}
                    className="w-full h-full cursor-pointer group/img"
                    onClick={() => openLightbox(slide.image)}
                    style={{ animation: 'fadeSlide 0.4s ease-out' }}
                  >
                    {imageErrors[slide.image] ? (
                      <ImagePlaceholder title={slide.title} icon={slide.icon} />
                    ) : (
                      <>
                        <img 
                          src={slide.image} 
                          alt={slide.title} 
                          className="w-full h-full object-cover object-top"
                          onError={() => handleImageError(slide.image)}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/20 transition-all flex items-center justify-center opacity-0 group-hover/img:opacity-100">
                          <div className="bg-black/60 backdrop-blur-sm rounded-full px-4 py-2 flex items-center gap-2">
                            <ZoomIn size={18} className="text-white" />
                            <span className="text-white text-sm font-medium">Click to expand</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
                <div className="hidden lg:block absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-slate-900/90 to-transparent" />
              </div>

              {/* Nav arrows */}
              <button 
                onClick={prev} 
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-cyan-500/30 flex items-center justify-center transition-colors backdrop-blur-sm border border-slate-700/50"
                data-testid="slide-prev-btn"
              >
                <ChevronLeft size={20} />
              </button>
              <button 
                onClick={() => { next(); setIsAutoPlaying(false); }} 
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-cyan-500/30 flex items-center justify-center transition-colors backdrop-blur-sm border border-slate-700/50"
                data-testid="slide-next-btn"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            {/* Feature description panel */}
            <div className="lg:w-1/3 p-6 lg:p-8 flex flex-col justify-center bg-gradient-to-b lg:bg-gradient-to-r from-slate-900/60 to-slate-900/90">
              <div key={current} style={{ animation: 'fadeUp 0.4s ease-out' }}>
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4`}
                  style={{ background: 'linear-gradient(135deg, rgba(6,182,212,0.15), rgba(59,130,246,0.15))' }}
                >
                  <Icon size={16} className="text-cyan-400" />
                  <span className="text-xs font-semibold text-cyan-300 uppercase tracking-wider">
                    {current + 1} of {slides.length}
                  </span>
                </div>
                <h2 className="text-2xl font-bold mb-3 text-white">{slide.title}</h2>
                <p className="text-slate-400 leading-relaxed text-sm">{slide.desc}</p>
              </div>
            </div>
          </div>

          {/* Dots navigation */}
          <div className="flex items-center justify-center gap-2 py-3 bg-slate-900/80 border-t border-slate-800/50 flex-wrap">
            {slides.map((s, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className="group flex items-center gap-1.5 px-2 py-1 rounded-md transition-all hover:bg-slate-800/50"
                data-testid={`slide-dot-${i}`}
              >
                <Circle size={8} className={`transition-all ${i === current ? 'fill-cyan-400 text-cyan-400' : 'fill-slate-600 text-slate-600 group-hover:fill-slate-400 group-hover:text-slate-400'}`} />
                <span className={`text-xs hidden sm:inline transition-colors ${i === current ? 'text-cyan-400 font-medium' : 'text-slate-500 group-hover:text-slate-400'}`}>
                  {s.title}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Auto-play indicator */}
        {isAutoPlaying && (
          <div className="mt-3 flex items-center justify-center">
            <div className="h-0.5 w-32 bg-slate-800 rounded-full overflow-hidden">
              <div key={current} className="h-full bg-cyan-500 rounded-full" style={{ animation: `progressBar ${AUTO_PLAY_INTERVAL}ms linear` }} />
            </div>
          </div>
        )}
      </div>

      {/* Features Grid */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 pb-14">
        <h2 className="text-xl font-bold text-center mb-8 text-slate-200">Everything You Need to Manage Your Finances</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {features.map((f, i) => (
            <div key={i} className="bg-slate-900/40 border border-slate-700/30 rounded-xl p-4 text-center hover:border-cyan-500/40 hover:bg-slate-800/40 transition-all group">
              <f.icon className="mx-auto text-cyan-400 mb-2 group-hover:scale-110 transition-transform" size={24} />
              <h3 className="font-semibold text-sm mb-1 text-slate-200">{f.title}</h3>
              <p className="text-slate-500 text-xs leading-snug">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="relative z-10 max-w-2xl mx-auto px-4 pb-16 text-center">
        <div className="bg-gradient-to-r from-cyan-900/20 to-blue-900/20 border border-cyan-700/25 rounded-2xl p-8">
          <h2 className="text-xl font-bold mb-2">{CTA_HEADLINE}</h2>
          <p className="text-slate-400 text-sm mb-5">{CTA_SUBTITLE}</p>
          <button
            onClick={() => navigate(CTA_LINK)}
            className="px-8 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold rounded-xl transition-all shadow-lg shadow-cyan-600/20"
            data-testid="learn-more-cta-btn"
          >
            {CTA_BUTTON_TEXT}
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="relative z-10 text-center pb-6">
        <p className="text-xs text-slate-600">
          © 2025/2026 RevTracker - Professional Financial Management
        </p>
      </div>

      {/* Lightbox Modal */}
      {lightboxIdx !== null && !imageErrors[allImages[lightboxIdx]?.src] && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          onClick={() => setLightboxIdx(null)}
          style={{ animation: 'fadeIn 0.2s ease-out' }}
        >
          <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" />
          <button 
            onClick={(e) => { e.stopPropagation(); lightboxPrev(); }} 
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-black/60 hover:bg-cyan-500/30 border border-slate-700 flex items-center justify-center transition-colors"
          >
            <ChevronLeft size={24} className="text-white" />
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); lightboxNext(); }} 
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-black/60 hover:bg-cyan-500/30 border border-slate-700 flex items-center justify-center transition-colors"
          >
            <ChevronRight size={24} className="text-white" />
          </button>
          <div className="relative max-w-[90vw] max-h-[92vh] mx-16" onClick={(e) => e.stopPropagation()}>
            <img
              key={lightboxIdx}
              src={allImages[lightboxIdx].src}
              alt="Expanded view"
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              style={{ animation: 'zoomIn 0.25s ease-out' }}
            />
            <button 
              onClick={() => setLightboxIdx(null)} 
              className="absolute -top-3 -right-3 w-10 h-10 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-full flex items-center justify-center transition-colors shadow-lg"
            >
              <X size={20} className="text-white" />
            </button>
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 rounded-b-lg flex items-end justify-between">
              <div>
                <p className="text-white text-sm font-medium">{allImages[lightboxIdx].title}</p>
                <p className="text-slate-300 text-xs mt-0.5">{allImages[lightboxIdx].desc}</p>
              </div>
              <span className="text-slate-400 text-xs whitespace-nowrap ml-4">{lightboxIdx + 1} / {allImages.length}</span>
            </div>
          </div>
        </div>
      )}

      {/* CSS animations */}
      <style>{`
        @keyframes fadeSlide {
          from { opacity: 0; transform: translateX(${direction > 0 ? '20px' : '-20px'}); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes progressBar {
          from { width: 0%; }
          to { width: 100%; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes zoomIn {
          from { opacity: 0; transform: scale(0.92); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
